<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>Support Our Mission</h2>
        <p>Help us provide technology to students across Brevard County</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <h2 style="text-align: center;">How to Donate</h2>
        <p style="text-align: center; margin-bottom: 2rem;">Choose how you'd like to support our mission</p>

        <div class="donation-options" style="display: flex; gap: 2rem; justify-content: center; margin: 2rem 0 3rem;">
          <button
            @click="currentSection = 'money'"
            class="btn btn-primary"
            :class="{ active: currentSection === 'money' }"
            style="cursor: pointer;">
            Donate Money
          </button>
          <button
            @click="currentSection = 'equipment'"
            class="btn btn-secondary"
            :class="{ active: currentSection === 'equipment' }"
            style="cursor: pointer;">
            Donate Equipment
          </button>
        </div>

        <!-- Money Donation Section -->
        <div v-if="currentSection === 'money'" id="moneySection">
          <h3 style="text-align: center; margin-bottom: 1.5rem;">Financial Donations</h3>

          <div class="program-grid" style="margin-bottom: 3rem;">
            <div class="program-card">
              <h4>Where Your Money Goes</h4>
              <p>Space Coast STEM is an entirely volunteer-run organization. Our board members personally cover all operational expenses including website hosting, insurance, and administrative costs.</p>

              <p style="margin-top: 1rem;"><strong>This means 100% of your donation goes directly toward:</strong></p>
              <ul>
                <li>Purchasing laptops and computer equipment for students</li>
                <li>Replacement parts for refurbishment projects</li>
                <li>Tools and materials for computer repairs</li>
              </ul>

              <p style="margin-top: 1.5rem;"><strong>Leveraging Bulk Buying Power:</strong></p>
              <p>By pooling donations together, we can purchase decommissioned school laptops in bulk from government surplus auctions at wholesale prices—as low as $5 per laptop when buying 500+. This means your donation can have a much greater impact than buying laptops individually. Learn more about <router-link to="/theplan" style="color: #3ba3d1; font-weight: 600;">our plan</router-link> to scale our impact.</p>
            </div>

            <div class="program-card" style="display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 1.5rem;">
              <div class="tip-notice">
                <h4 style="margin: 0 0 0.5rem 0; color: #e67e22; font-size: 1rem;">
                  <span style="font-size: 1.2rem;">⚠️</span> About the Platform Tip
                </h4>
                <p style="margin: 0; font-size: 0.9rem; line-height: 1.6;">
                  Givebutter waives their platform fee when we give donors the <strong>option</strong> to leave a tip. The default tip is <strong>15%</strong>
                  but you can adjust this to any amount (including <strong>$0</strong>) by clicking the tip ammount and then "<strong>Other</strong>" in the tip section.
                </p>
              </div>
              <givebutter-widget id="LPRmnp"></givebutter-widget>
            </div>
          </div>
        </div>

        <!-- Equipment Donation Section -->
        <div v-if="currentSection === 'equipment'" id="equipmentSection">
          <h3 style="text-align: center; margin-bottom: 1.5rem;">Equipment Donations</h3>

          <div class="program-grid" style="margin-bottom: 3rem;">
            <div class="program-card">
              <h4>What We Accept</h4>
              <p>We accept donations of working computers and peripherals that can be refurbished and given to students in need.</p>
              <ul style="margin-top: 1rem;">
                <li>Desktop computers</li>
                <li>Laptops</li>
                <li>Computer peripherals (keyboards, mice, monitors)</li>
                <li>Replacement parts (RAM, storage drives, batteries)</li>
                <li>Other technology equipment (tablets, routers)</li>
              </ul>
            </div>

            <div class="program-card">
              <h4>Minimum Requirements for Computers</h4>
              <p><strong>Laptops:</strong></p>
              <ul>
                <li>Intel Core i3 (3rd gen+) or equivalent AMD</li>
                <li>4GB RAM minimum (8GB preferred)</li>
                <li>Working battery that holds a charge</li>
                <li>Functional screen, keyboard, and trackpad</li>
              </ul>

              <p style="margin-top: 1rem;"><strong>Desktops:</strong></p>
              <ul>
                <li>Intel Core i3 (3rd gen+) or equivalent AMD</li>
                <li>4GB RAM minimum (8GB preferred)</li>
                <li>All components functioning properly</li>
              </ul>
            </div>
          </div>

          <div class="contact-form">
            <h4 style="text-align: center; margin-bottom: 1.5rem;">Equipment Donation Form</h4>
            <p style="text-align: center; margin-bottom: 2rem;">Please email us at <strong>donations@spacecoaststem.org</strong> with details about the equipment you'd like to donate. Include:</p>
            <ul style="max-width: 600px; margin: 0 auto 2rem; text-align: left;">
              <li>Your contact information</li>
              <li>Device type, brand, and model</li>
              <li>Specifications (processor, RAM, storage)</li>
              <li>Condition of the device</li>
              <li>Number of devices</li>
              <li>Your location in Brevard County</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="impact-section">
      <div class="container">
        <h2>Impact of Your Donation</h2>
        <p>Every donation makes a real difference in a student's life:</p>
        <div class="impact-grid">
          <div class="impact-card">
            <div class="impact-icon">💻</div>
            <h3>Access to Technology</h3>
            <p>Students receive laptops they can use for homework, research, and learning new skills</p>
          </div>
          <div class="impact-card">
            <div class="impact-icon">📚</div>
            <h3>Educational Opportunity</h3>
            <p>Technology opens doors to online courses, educational resources, and college applications</p>
          </div>
          <div class="impact-card">
            <div class="impact-icon">♻️</div>
            <h3>Environmental Impact</h3>
            <p>Equipment donations keep computers out of landfills while serving students</p>
          </div>
          <div class="impact-card">
            <div class="impact-icon">🌟</div>
            <h3>Future Success</h3>
            <p>Digital literacy prepares students for college and careers</p>
          </div>
        </div>
      </div>
    </section>

    <section class="cta">
      <div class="container">
        <h2>Questions About Donating?</h2>
        <p>Contact us to learn more about donations or other ways to support our mission</p>
        <div class="cta-buttons">
          <router-link to="/contact" class="btn btn-primary">Contact Us</router-link>
          <router-link to="/about" class="btn btn-secondary">Learn More About Us</router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'

const route = useRoute()
const currentSection = ref<'money' | 'equipment'>('money')

onMounted(() => {
  // Check for section query parameter
  const section = route.query.section as string
  if (section === 'equipment') {
    currentSection.value = 'equipment'
  } else if (section === 'money') {
    currentSection.value = 'money'
  }

  // Load Givebutter widget script if not already loaded
  if (!document.querySelector('script[src*="widgets.givebutter.com"]')) {
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://widgets.givebutter.com/latest.umd.cjs?acct=ksC8Ep1sarshQQeS&p=other'
    document.head.appendChild(script)
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

.content-section {
  padding: 5rem 2rem;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section h3 {
  font-size: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

/* Program Grid */
.program-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.program-card {
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
}

.program-card:hover {
  transform: translateY(-5px);
}

.program-card h4 {
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 1rem 0;
  font-family: 'Poppins', sans-serif;
}

.program-card p {
  color: var(--text-light, #666666);
  line-height: 1.6;
}

.program-card ul {
  margin-left: 1.5rem;
  line-height: 1.8;
  color: var(--text-light, #666666);
}

/* Contact Form */
.contact-form {
  max-width: 800px;
  margin: 2rem auto;
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.contact-form h4 {
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.contact-form p {
  color: var(--text-light, #666666);
  line-height: 1.6;
}

.contact-form ul {
  color: var(--text-light, #666666);
  line-height: 1.8;
}

.tip-notice {
  background-color: #fff3cd;
  border: 2px solid #e67e22;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  width: 100%;
  max-width: 500px;
}

.tip-notice h4 {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.impact-section {
  padding: 5rem 2rem;
  background: var(--light-bg, #f5f7fa);
}

.impact-section h2 {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.impact-section > .container > p {
  text-align: center;
  font-size: 1.1rem;
  margin-bottom: 3rem;
  color: var(--text-light, #666666);
}

.impact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.impact-card {
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.impact-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.impact-card h3 {
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

.impact-card p {
  color: var(--text-light, #666666);
  line-height: 1.6;
}

.cta {
  background: var(--dark-bg, #1f3447);
  color: var(--white, #ffffff);
  padding: 5rem 2rem;
  text-align: center;
}

.cta h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

.cta p {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border: 2px solid transparent;
}

/* Tab buttons (for donation options) */
.donation-options .btn-primary,
.donation-options .btn-secondary {
  background: #e8f0f7;
  color: var(--primary-color, #2c4a6b);
  border: 2px solid var(--primary-color, #2c4a6b);
}

.donation-options .btn-primary:hover,
.donation-options .btn-secondary:hover {
  background: #d0e3f2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 74, 107, 0.2);
}

.donation-options .btn-primary.active,
.donation-options .btn-secondary.active {
  background: var(--primary-color, #2c4a6b);
  color: var(--white, #ffffff);
  box-shadow: 0 4px 15px rgba(44, 74, 107, 0.3);
  border-color: var(--primary-color, #2c4a6b);
}

/* CTA buttons (at bottom of page) */
.cta .btn-primary {
  background: var(--accent-color, #e94f3a);
  color: var(--white, #ffffff);
}

.cta .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(233, 79, 58, 0.3);
}

.cta .btn-secondary {
  border-color: var(--white, #ffffff);
  background: transparent;
  color: var(--white, #ffffff);
}

.cta .btn-secondary:hover {
  background: var(--white, #ffffff);
  color: var(--primary-color, #2c4a6b);
}

@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2, .impact-section h2, .cta h2 {
    font-size: 2rem;
  }

  .donation-grid {
    grid-template-columns: 1fr;
  }

  .impact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
